import Navigation from '../../components/feature/Navigation';
import SEOHead from '../../components/feature/SEOHead';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ResearchSection from './components/ResearchSection';
import PublicationsSection from './components/PublicationsSection';
import AffiliationsSection from './components/AffiliationsSection';
import PressSection from './components/PressSection';
import ContactSection from './components/ContactSection';

const personSchema = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': 'https://oludamolaadebowale.com/#person',
  name: 'Oludamola Adebowale',
  givenName: 'Oludamola',
  familyName: 'Adebowale',
  honorificSuffix: 'FRSA, AFRHistS',
  description:
    'Oludamola Adebowale, FRSA, AFRHistS, is a Nigerian archivist, public historian, writer, curator, and cultural heritage specialist dedicated to the preservation, documentation, and promotion of African history and cultural memory. He is a Fellow of the Royal Society of Arts (RSA), an Associate Fellow of the Royal Historical Society (UK), and a Member of the International Council on Archives (ICA). He is the Founder and Creative Director of ASIRI Magazine, and Senior Curator of the Nigerian-Brazilian Public History Project.',
  image:
    'https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/fc3c1659-262f-4cf2-ae3e-f92264656772_Fanyogo-4939.jpg?v=a74bd6518f3f85a5150853ab266281ff',
  url: 'https://oludamolaadebowale.com',
  nationality: 'Nigerian',
  knowsAbout: [
    'African history',
    'Nigerian cultural heritage',
    'Archival preservation',
    'Public history',
    'Cultural memory',
    'Museology',
    'Curatorial practice',
    'Oral history',
    'Digital humanities',
    'Yoruba culture',
  ],
  hasOccupation: [
    { '@type': 'Occupation', name: 'Archivist' },
    { '@type': 'Occupation', name: 'Curator' },
    { '@type': 'Occupation', name: 'Historian' },
    { '@type': 'Occupation', name: 'Writer' },
    { '@type': 'Occupation', name: 'Cultural Heritage Specialist' },
  ],
  alumniOf: [
    {
      '@type': 'EducationalOrganization',
      name: 'Obafemi Awolowo University',
      description: 'Estate Management',
    },
    {
      '@type': 'EducationalOrganization',
      name: 'Harvard Law School',
      description: 'CopyrightX Program',
    },
    {
      '@type': 'EducationalOrganization',
      name: 'University of Lagos',
      description: 'Institute of Advanced Legal Studies — Copyright and Intellectual Property Law',
    },
    {
      '@type': 'EducationalOrganization',
      name: 'Orange Academy',
      description: 'Integrated Branding and Copywriting',
    },
    {
      '@type': 'EducationalOrganization',
      name: 'Lagos Entrepreneurial Business School',
      description: 'Business and Marketing Development',
    },
  ],
  worksFor: [
    {
      '@type': 'Organization',
      name: 'ASIRI Magazine',
      description: 'Nigerian platform dedicated to history, archival preservation, cultural heritage, and long-form historical storytelling',
      url: 'https://asirimagazine.com',
    },
    {
      '@type': 'Organization',
      name: 'Nigerian-Brazilian Public History Project',
      description: 'Initiative documenting, preserving, and promoting the history of the Afro-Brazilian community in Lagos and Brazil',
    },
  ],
  memberOf: [
    {
      '@type': 'Organization',
      name: 'Royal Society of Arts',
      alternateName: 'RSA',
      url: 'https://www.thersa.org',
    },
    {
      '@type': 'Organization',
      name: 'Royal Historical Society',
      alternateName: 'AFRHistS',
      url: 'https://royalhistsoc.org',
    },
    {
      '@type': 'Organization',
      name: 'International Council on Archives',
      alternateName: 'ICA',
      url: 'https://www.ica.org',
    },
  ],
  sameAs: [
    'https://en.wikipedia.org/wiki/Oludamola_Adebowale',
    'https://www.linkedin.com/in/oludamola-adebowale-frhists-2a10b52a/',
    'https://oauife.academia.edu/OludamolaAdebowale',
    'https://oludamolaadebowale.com',
  ],
  author: [
    {
      '@type': 'Book',
      name: 'The Fabric of a Nation: Essays on Nigeria\'s History, Arts and Culture',
      author: { '@id': 'https://oludamolaadebowale.com/#person' },
      description:
        'A collection of essays and scholarly reflections examining Nigerian history, heritage, identity, arts, and cultural development.',
    },
  ],
};

const profilePageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': 'https://oludamolaadebowale.com/#profilepage',
  url: 'https://oludamolaadebowale.com',
  name: 'Oludamola Adebowale | Nigerian Cultural Historian, Curator & Archivist',
  description:
    'Official website of Oludamola Adebowale, FRSA, AFRHistS — Nigerian archivist, public historian, curator, and cultural heritage specialist. Founder of ASIRI Magazine, Senior Curator of the Nigerian-Brazilian Public History Project, and author of The Fabric of a Nation.',
  mainEntity: { '@id': 'https://oludamolaadebowale.com/#person' },
  about: { '@id': 'https://oludamolaadebowale.com/#person' },
  inLanguage: 'en',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://oludamolaadebowale.com/',
      },
    ],
  },
};

export default function Home() {
  return (
    <div
      className="relative"
      itemScope
      itemType="https://schema.org/ProfilePage"
    >
      <SEOHead
        title="Oludamola Adebowale | Nigerian Cultural Historian, Curator & Archivist"
        description="Official website of Oludamola Adebowale, FRSA, AFRHistS — Nigerian archivist, public historian, curator, and cultural heritage specialist. Founder of ASIRI Magazine, Senior Curator of the Nigerian-Brazilian Public History Project, and author of The Fabric of a Nation."
        keywords="Oludamola Adebowale, Nigerian cultural historian, African archivist, ASIRI Magazine, cultural heritage Nigeria, public historian Lagos, AFRHistS, Royal Society of Arts"
        canonical="https://oludamolaadebowale.com/"
        ogType="profile"
        ogImage="https://static.readdy.ai/image/aed34eea4e280644dc241a74366b8cb3/a218dde90e537f98cb78b1a5590e8cdd.png"
        schemaJson={[personSchema, profilePageSchema]}
      />
      <Navigation />
      <header>
        <HeroSection />
      </header>
      <main itemScope itemType="https://schema.org/Person" itemID="https://oludamolaadebowale.com/#person">
        <meta itemProp="name" content="Oludamola Adebowale" />
        <meta itemProp="jobTitle" content="Cultural Historian, Curator, Archivist" />
        <meta itemProp="nationality" content="Nigerian" />
        <AboutSection />
        <ResearchSection />
        <PublicationsSection />
        <AffiliationsSection />
        <PressSection />
      </main>
      <ContactSection />
    </div>
  );
}